import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Bold,
  Code2,
  Heading2,
  Italic,
  Link,
  List,
  ListOrdered,
  Quote,
  Strikethrough,
  Superscript,
  Table2,
} from "lucide-react";

const inlinePattern = /(\*\*[^*]+\*\*|~~[^~]+~~|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^\s)]+\)|\^[^^]+\^|_[^_]+_)/g;

const escapeHtml = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function inlineMarkdownToHtml(value: string) {
  return value.split(inlinePattern).filter(Boolean).map((part) => {
    if (part.startsWith("**") && part.endsWith("**")) return `<strong>${escapeHtml(part.slice(2, -2))}</strong>`;
    if (part.startsWith("~~") && part.endsWith("~~")) return `<del>${escapeHtml(part.slice(2, -2))}</del>`;
    if (part.startsWith("`") && part.endsWith("`")) return `<code>${escapeHtml(part.slice(1, -1))}</code>`;
    if (part.startsWith("^") && part.endsWith("^")) return `<sup>${escapeHtml(part.slice(1, -1))}</sup>`;
    if (part.startsWith("_") && part.endsWith("_")) return `<em>${escapeHtml(part.slice(1, -1))}</em>`;
    const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
    if (link) return `<a href="${escapeHtml(link[2])}" target="_blank" rel="noreferrer">${escapeHtml(link[1])}</a>`;
    return escapeHtml(part);
  }).join("");
}

export function markdownToEditorHtml(value: string) {
  const lines = value.split("\n");
  const blocks: string[] = [];
  for (let index = 0; index < lines.length;) {
    const line = lines[index];
    if (!line.trim()) { index += 1; continue; }
    if (/^\|.+\|$/.test(line) && /^\|(?:\s*:?-+:?\s*\|)+$/.test(lines[index + 1] || "")) {
      const headers = line.slice(1, -1).split("|").map((cell) => cell.trim());
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && /^\|.+\|$/.test(lines[index])) {
        rows.push(lines[index].slice(1, -1).split("|").map((cell) => cell.trim()));
        index += 1;
      }
      blocks.push(`<table><thead><tr>${headers.map((cell) => `<th>${inlineMarkdownToHtml(cell)}</th>`).join("")}</tr></thead><tbody>${rows.map((row) => `<tr>${row.map((cell) => `<td>${inlineMarkdownToHtml(cell)}</td>`).join("")}</tr>`).join("")}</tbody></table>`);
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = lines[index].match(/^[-*]\s+(.+)/);
        if (!match) break;
        items.push(match[1]);
        index += 1;
      }
      blocks.push(`<ul>${items.map((item) => `<li>${inlineMarkdownToHtml(item)}</li>`).join("")}</ul>`);
      continue;
    }
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length) {
        const match = lines[index].match(/^\d+\.\s+(.+)/);
        if (!match) break;
        items.push(match[1]);
        index += 1;
      }
      blocks.push(`<ol>${items.map((item) => `<li>${inlineMarkdownToHtml(item)}</li>`).join("")}</ol>`);
      continue;
    }
    if (line.startsWith("## ")) blocks.push(`<h2>${inlineMarkdownToHtml(line.slice(3))}</h2>`);
    else if (line.startsWith("> ")) blocks.push(`<blockquote>${inlineMarkdownToHtml(line.slice(2))}</blockquote>`);
    else blocks.push(`<p>${inlineMarkdownToHtml(line)}</p>`);
    index += 1;
  }
  return blocks.join("");
}

function inlineNodeToMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
  if (!(node instanceof HTMLElement)) return "";
  const content = Array.from(node.childNodes).map(inlineNodeToMarkdown).join("");
  switch (node.tagName) {
    case "STRONG": case "B": return `**${content}**`;
    case "EM": case "I": return `_${content}_`;
    case "DEL": case "S": case "STRIKE": return `~~${content}~~`;
    case "SUP": return `^${content}^`;
    case "CODE": return `\`${content}\``;
    case "A": return `[${content}](${node.getAttribute("href") || ""})`;
    case "BR": return "\n";
    default: return content;
  }
}

function blockNodeToMarkdown(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || "";
  if (!(node instanceof HTMLElement)) return "";
  const content = () => Array.from(node.childNodes).map(inlineNodeToMarkdown).join("").trim();
  switch (node.tagName) {
    case "H2": return `## ${content()}`;
    case "BLOCKQUOTE": return `> ${content()}`;
    case "UL": return Array.from(node.children).map((item) => `- ${Array.from(item.childNodes).map(inlineNodeToMarkdown).join("").trim()}`).join("\n");
    case "OL": return Array.from(node.children).map((item, index) => `${index + 1}. ${Array.from(item.childNodes).map(inlineNodeToMarkdown).join("").trim()}`).join("\n");
    case "TABLE": {
      const rows = Array.from(node.querySelectorAll("tr")).map((row) => Array.from(row.children).map((cell) => Array.from(cell.childNodes).map(inlineNodeToMarkdown).join("").trim()));
      if (!rows.length) return "";
      return [`| ${rows[0].join(" | ")} |`, `| ${rows[0].map(() => "---").join(" | ")} |`, ...rows.slice(1).map((row) => `| ${row.join(" | ")} |`)].join("\n");
    }
    case "DIV": case "P": return content();
    default: return inlineNodeToMarkdown(node);
  }
}

export function editorHtmlToMarkdown(html: string) {
  const container = document.createElement("div");
  container.innerHTML = html;
  return Array.from(container.childNodes)
    .map(blockNodeToMarkdown)
    .filter((block) => block.trim())
    .join("\n")
    .replace(/\u00a0/g, " ")
    .replace(/\u200b/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

type Props = { value: string; onChange: (value: string) => void };

export function RichMarkdownEditor({ value, onChange }: Props) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(() => new Set());

  const refreshFormats = useCallback(() => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    const anchorNode = selection?.anchorNode;
    if (!editor || !anchorNode || !editor.contains(anchorNode)) {
      setActiveFormats((current) => current.size ? new Set() : current);
      return;
    }
    const anchorElement = anchorNode instanceof Element ? anchorNode : anchorNode.parentElement;
    const next = new Set<string>();
    if (document.queryCommandState("bold")) next.add("bold");
    if (document.queryCommandState("italic")) next.add("italic");
    if (document.queryCommandState("strikeThrough")) next.add("strikeThrough");
    if (document.queryCommandState("superscript")) next.add("superscript");
    if (document.queryCommandState("insertUnorderedList")) next.add("unorderedList");
    if (document.queryCommandState("insertOrderedList")) next.add("orderedList");
    const block = String(document.queryCommandValue("formatBlock") || "").toLowerCase().replace(/[<>]/g, "");
    if (block === "h2") next.add("heading");
    if (block === "blockquote") next.add("quote");
    if (anchorElement?.closest("code")) next.add("code");
    if (anchorElement?.closest("a")) next.add("link");
    if (anchorElement?.closest("table")) next.add("table");
    setActiveFormats((current) => {
      if (current.size === next.size && Array.from(current).every((format) => next.has(format))) return current;
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (editor && document.activeElement !== editor) editor.innerHTML = markdownToEditorHtml(value);
  }, [value]);

  useEffect(() => {
    editorRef.current?.focus();
    refreshFormats();
  }, [refreshFormats]);

  useEffect(() => {
    document.addEventListener("selectionchange", refreshFormats);
    return () => document.removeEventListener("selectionchange", refreshFormats);
  }, [refreshFormats]);

  const emitChange = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const next = editorHtmlToMarkdown(editor.innerHTML);
    if (!next && editor.innerHTML) editor.innerHTML = "";
    onChange(next);
  };
  const prepareEditor = () => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();
    const selection = window.getSelection();
    if (!selection?.rangeCount || !editor.contains(selection.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };
  const applyInline = (command: string) => {
    prepareEditor();
    const selection = window.getSelection();
    const hasSelection = Boolean(selection && !selection.isCollapsed);
    document.execCommand(command, false);
    if (hasSelection) emitChange();
    refreshFormats();
  };
  const applyBlock = (command: string, value?: string) => {
    prepareEditor();
    const hasContent = Boolean(editorRef.current?.innerText.trim());
    document.execCommand(command, false, value);
    if (hasContent) emitChange();
    refreshFormats();
  };
  const insertHtml = (html: string) => {
    prepareEditor();
    document.execCommand("insertHTML", false, html);
    emitChange();
    refreshFormats();
  };
  const insertLink = () => {
    prepareEditor();
    const selection = window.getSelection();
    if (!selection) return;
    const href = window.prompt("Informe o endereço do link:", "https://");
    if (!href || !/^https?:\/\//i.test(href)) return;
    if (!selection.isCollapsed) {
      document.execCommand("createLink", false, href);
      emitChange();
      refreshFormats();
      return;
    }
    const range = selection.getRangeAt(0);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.append(document.createTextNode("\u200b"));
    range.insertNode(anchor);
    range.selectNodeContents(anchor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    refreshFormats();
  };
  const insertCode = () => {
    prepareEditor();
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const wasCollapsed = selection.isCollapsed;
    const range = selection.getRangeAt(0);
    const activeCode = selection.anchorNode instanceof Element
      ? selection.anchorNode.closest("code")
      : selection.anchorNode?.parentElement?.closest("code");
    if (wasCollapsed && activeCode) {
      range.setStartAfter(activeCode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      if (!activeCode.textContent?.replace(/\u200b/g, "")) activeCode.remove();
      refreshFormats();
      return;
    }
    const code = document.createElement("code");
    if (wasCollapsed) code.append(document.createTextNode("\u200b"));
    else code.append(range.extractContents());
    range.insertNode(code);
    range.selectNodeContents(code);
    if (wasCollapsed) range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
    if (!wasCollapsed) emitChange();
    refreshFormats();
  };
  const toolbarButton = (title: string, format: string, action: () => void, icon: React.ReactNode) => (
    <button type="button" title={title} aria-label={title} aria-pressed={activeFormats.has(format)} className={activeFormats.has(format) ? "active" : undefined} onMouseDown={(event) => event.preventDefault()} onClick={action}>{icon}</button>
  );

  return <>
    <div className="triage-rich-toolbar" role="toolbar" aria-label="Formatação do parecer">
      {toolbarButton("Negrito", "bold", () => applyInline("bold"), <Bold/>)}
      {toolbarButton("Itálico", "italic", () => applyInline("italic"), <Italic/>)}
      {toolbarButton("Tachado", "strikeThrough", () => applyInline("strikeThrough"), <Strikethrough/>)}
      {toolbarButton("Sobrescrito", "superscript", () => applyInline("superscript"), <Superscript/>)}
      {toolbarButton("Título", "heading", () => applyBlock("formatBlock", "h2"), <Heading2/>)}
      {toolbarButton("Link", "link", insertLink, <Link/>)}
      {toolbarButton("Lista", "unorderedList", () => applyBlock("insertUnorderedList"), <List/>)}
      {toolbarButton("Lista numerada", "orderedList", () => applyBlock("insertOrderedList"), <ListOrdered/>)}
      {toolbarButton("Citação", "quote", () => applyBlock("formatBlock", "blockquote"), <Quote/>)}
      {toolbarButton("Código", "code", insertCode, <Code2/>)}
      {toolbarButton("Tabela", "table", () => insertHtml("<table><thead><tr><th><br></th><th><br></th></tr></thead><tbody><tr><td><br></td><td><br></td></tr></tbody></table>"), <Table2/>)}
    </div>
    <div className="triage-opinion-workspace">
      <div
        ref={editorRef}
        className="triage-opinion-editor formatted-text-preview"
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Parecer da triagem"
        data-placeholder="Escreva o parecer da triagem…"
        onFocus={refreshFormats}
        onInput={() => { emitChange(); refreshFormats(); }}
        onKeyUp={refreshFormats}
        onMouseUp={refreshFormats}
      />
    </div>
  </>;
}
